import { cn } from '@/lib/utils'
import type { NewsletterSubscribeVariant } from '@tipi/shared'

import { SubscribeForm } from './SubscribeForm'
import { resolveNewsletterEyebrow, resolveNewsletterSubtitle, resolveNewsletterTitle } from './newsletterCopy'

type NewsletterSubscribeProps = {
    variant: NewsletterSubscribeVariant
    source: string
    hub?: 'aprender' | 'actualidad'
    /** Landing variant only: slim strip without the mascot or subtitle, for the end of long pages. */
    compact?: boolean
    className?: string
}

export function NewsletterSubscribe({ variant, source, hub, compact = false, className }: NewsletterSubscribeProps) {
    const eyebrow = resolveNewsletterEyebrow(variant)
    const title = resolveNewsletterTitle({ variant, hub })
    const subtitle = resolveNewsletterSubtitle(variant)

    if (variant === 'article') {
        return (
            <section className={cn('mx-auto mt-9 max-w-[680px] border-t-2 border-ink pb-[72px] pt-8', className)}>
                <div className='flex flex-col gap-3.5'>
                    <p className='m-0 text-xs font-bold uppercase tracking-caps text-flame-press'>{eyebrow}</p>
                    <h2 className='m-0 text-balance text-[26px] font-extrabold leading-[30px] tracking-display text-ink md:text-[32px] md:leading-9'>
                        {title}
                    </h2>
                    <p className='m-0 mb-2.5 max-w-none text-base leading-6 text-ink-2 md:text-[17px] md:leading-[26px]'>
                        {subtitle}
                    </p>
                    <SubscribeForm variant={variant} source={source} />
                </div>
            </section>
        )
    }

    if (variant === 'hub') {
        return (
            <section className={cn('bg-cream py-[88px]', className)}>
                <div className='mx-auto grid max-w-[1120px] items-start gap-10 px-5 lg:grid-cols-[1.1fr_1fr] lg:gap-20'>
                    <div className='flex flex-col gap-3.5'>
                        <p className='m-0 text-xs font-bold uppercase tracking-caps text-ink'>{eyebrow}</p>
                        <h2 className='m-0 text-balance text-[36px] font-extrabold leading-[0.98] tracking-display text-ink md:text-[46px]'>
                            {title}
                        </h2>
                        <p className='m-0 max-w-[480px] text-[17px] leading-[26px] text-ink-2 md:text-lg md:leading-7'>
                            {subtitle}
                        </p>
                    </div>
                    <SubscribeForm variant={variant} source={source} className='lg:pt-7' />
                </div>
            </section>
        )
    }

    if (compact) {
        return (
            <section className={cn('px-5 py-6 md:py-8', className)}>
                <div className='mx-auto grid max-w-[1120px] items-center gap-4 rounded-panel bg-cream px-5 py-5 md:grid-cols-[1fr_1fr] md:gap-10 md:px-8 md:py-6'>
                    <div className='flex flex-col gap-1.5'>
                        <p className='m-0 text-[11px] font-bold uppercase tracking-caps text-flame-press'>{eyebrow}</p>
                        <h2 className='m-0 text-balance text-[20px] font-extrabold leading-tight tracking-display text-ink md:text-[24px]'>
                            {title}
                        </h2>
                    </div>
                    <SubscribeForm variant={variant} source={source} />
                </div>
            </section>
        )
    }

    return (
        <section className={cn('px-5 py-[72px] pb-[88px]', className)}>
            <div className='mx-auto grid max-w-[1120px] items-center gap-8 rounded-panel bg-cream p-8 md:p-14 lg:grid-cols-[140px_1fr_0.95fr] lg:gap-11'>
                <img
                    src='/newsletter/mascota-waving.png'
                    alt=''
                    width={140}
                    height={152}
                    className='h-24 w-[96px] object-contain lg:h-[152px] lg:w-[140px] lg:self-start'
                />
                <div className='flex flex-col gap-3.5'>
                    <p className='m-0 text-xs font-bold uppercase tracking-caps text-flame-press'>{eyebrow}</p>
                    <h2 className='m-0 text-balance text-[32px] font-extrabold leading-10 tracking-display text-ink md:text-[40px]'>
                        {title}
                    </h2>
                    <p className='m-0 text-[17px] leading-[26px] text-ink-2'>{subtitle}</p>
                </div>
                <SubscribeForm variant={variant} source={source} />
            </div>
        </section>
    )
}
